import React, { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useMedia } from "react-use";

import { Text } from "@/components/Text";
import { TextListWithDot } from "@/components/TextListWithDot";
import { MEDIA_SIZE_SM } from "@/core/media";
import { Flex } from "@/eworkitComponents";

import privatePersonImg from "./assets/private-person.svg";
import supplierImg from "./assets/supplier.svg";
import { ProfileTypePickerBox } from "./components/ProfileTypePickerBox";

export type ProfileType = "supplier" | "private-person";

interface IProfileTypePickerProps {
  onProfileSelect: (contextType: ProfileType) => void;
}

export const ProfileTypePicker: FC<IProfileTypePickerProps> = ({ onProfileSelect }) => {
  const { t } = useTranslation("session");
  const isSmallScreen = useMedia(MEDIA_SIZE_SM);

  const renderSupplierText = useCallback(
    () => (
      <Flex direction="column" innerMargin="sm">
        <Text color="grey1" size="md">
          {t("ONBOARDING.PROFILE_TYPE_SELECT.SUPPLIER.TEXT")}
        </Text>
        <TextListWithDot direction="column" itemAlign="flex-start">
          <Text color="grey1" size="md">
            {t("ONBOARDING.PROFILE_TYPE_SELECT.SUPPLIER.TEXT_OPTION_1")}
          </Text>
          <Text color="grey1" size="md">
            {t("ONBOARDING.PROFILE_TYPE_SELECT.SUPPLIER.TEXT_OPTION_2")}
          </Text>
        </TextListWithDot>
      </Flex>
    ),
    [t],
  );

  const renderPrivatePersonText = useCallback(
    () => (
      <Flex direction="column">
        <Text color="grey1" size="md">
          {t("ONBOARDING.PROFILE_TYPE_SELECT.PRIVATE_PERSON.TEXT")}
        </Text>
      </Flex>
    ),
    [t],
  );

  return (
    <Flex direction={isSmallScreen ? "column" : "row"} innerMargin="xl">
      <Flex width={{ xxs: 12, sm: 6 }}>
        <ProfileTypePickerBox
          header={t("ONBOARDING.PROFILE_TYPE_SELECT.SUPPLIER.HEADER")}
          vatInfo={t("ONBOARDING.PROFILE_TYPE_SELECT.SUPPLIER.VAT_INFO")}
          renderText={renderSupplierText}
          buttonText={t("ONBOARDING.PROFILE_TYPE_SELECT.SUPPLIER.BUTTON_CREATE")}
          imgSrc={supplierImg}
          onClick={() => onProfileSelect("supplier")}
        />
      </Flex>
      <Flex width={{ xxs: 12, sm: 6 }}>
        <ProfileTypePickerBox
          header={t("ONBOARDING.PROFILE_TYPE_SELECT.PRIVATE_PERSON.HEADER")}
          vatInfo={t("ONBOARDING.PROFILE_TYPE_SELECT.PRIVATE_PERSON.VAT_INFO")}
          renderText={renderPrivatePersonText}
          buttonText={t("ONBOARDING.PROFILE_TYPE_SELECT.PRIVATE_PERSON.BUTTON_CREATE")}
          imgSrc={privatePersonImg}
          onClick={() => onProfileSelect("private-person")}
        />
      </Flex>
    </Flex>
  );
};
